/**
 * Base58 encoding/decoding using the Bitcoin alphabet.
 *
 * Encodes arbitrary byte arrays into URL-safe, human-readable strings
 * without ambiguous characters (0/O, l/1, +, /).
 *
 * @module Base58
 * @see docs/project/prefixed-ids.md
 */

const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const BASE = 58;

const ALPHABET_MAP = new Map<string, number>();
for (let i = 0; i < ALPHABET.length; i++) {
  ALPHABET_MAP.set(ALPHABET[i]!, i);
}

/** Encode a Uint8Array into a base58 string. */
export function encodeBase58(bytes: Uint8Array): string {
  if (bytes.length === 0) return '';

  // Count leading zero bytes (they map to '1' in base58)
  let leadingZeros = 0;
  while (leadingZeros < bytes.length && bytes[leadingZeros] === 0) {
    leadingZeros++;
  }

  // Work on a copy as a big-endian number
  const digits: number[] = [];
  for (let i = leadingZeros; i < bytes.length; i++) {
    let carry = bytes[i]!;
    for (let j = 0; j < digits.length; j++) {
      carry += digits[j]! * 256;
      digits[j] = carry % BASE;
      carry = Math.floor(carry / BASE);
    }
    while (carry > 0) {
      digits.push(carry % BASE);
      carry = Math.floor(carry / BASE);
    }
  }

  // Build result: leading '1's + reversed digits
  let result = ALPHABET[0]!.repeat(leadingZeros);
  for (let i = digits.length - 1; i >= 0; i--) {
    result += ALPHABET[digits[i]!];
  }

  return result;
}

/** Decode a base58 string back to a Uint8Array. Returns null on invalid input. */
export function decodeBase58(str: string): Uint8Array | null {
  if (str.length === 0) return new Uint8Array(0);

  // Count leading '1's (they map to 0x00 bytes)
  let leadingOnes = 0;
  while (leadingOnes < str.length && str[leadingOnes] === ALPHABET[0]) {
    leadingOnes++;
  }

  const bytes: number[] = [];
  for (let i = leadingOnes; i < str.length; i++) {
    const value = ALPHABET_MAP.get(str[i]!);
    if (value === undefined) return null; // invalid character

    let carry = value;
    for (let j = 0; j < bytes.length; j++) {
      carry += bytes[j]! * BASE;
      bytes[j] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }

  // Combine leading zeros + reversed bytes
  const result = new Uint8Array(leadingOnes + bytes.length);
  // leadingOnes positions are already 0
  for (let i = 0; i < bytes.length; i++) {
    result[leadingOnes + i] = bytes[bytes.length - 1 - i]!;
  }

  return result;
}
